import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';

const LINK_TYPES = [
  { value: 'trip', label: 'Trip' },
  { value: 'booking', label: 'Booking' },
  { value: 'draft_booking', label: 'Draft' },
  { value: 'lead', label: 'Booking Lead' },
  { value: 'vehicle_lead', label: 'Vehicle Lead' },
  { value: 'transporter', label: 'Transporter' },
];

const HINTS = {
  trip: 'Active trips, plus the last 2 completed, for this customer, driver, or transporter.',
  booking: 'This customer first. If they have fewer than 5, the rest are the latest bookings.',
  draft_booking: 'This customer first. If they have fewer than 5, the rest are the latest drafts.',
  lead: 'Customer booking leads for this phone/customer first. Or type a booking lead id.',
  vehicle_lead: 'Driver/transporter vehicle leads for this phone first. Or type a vehicle lead id.',
  transporter: 'The transporter matched to this caller. Or type an id.',
};

function hintFor(_log, entityType) {
  return HINTS[entityType];
}

export function callNeedsManualLink(log) {
  if (!log || log.status !== 'completed') return false;
  if (log.link_status === 'confirmed') return false;
  const type = log.linked_entity_type || '';
  return type === '' || type === 'general_enquiry' || type === 'unknown';
}

export function callCanRelink(log) {
  if (!log || log.status !== 'completed') return false;
  if (log.link_status === 'confirmed') return false;
  if (callNeedsManualLink(log)) return false;
  const by = String(log.linked_by || '').toLowerCase();
  return by === 'llm' || log.link_status === 'auto' || log.link_status === 'needs_review';
}

function applyLinkUpdate(log, data) {
  return {
    ...log,
    linked_entity_type: data.linked_entity_type,
    linked_entity_id: data.linked_entity_id,
    link_status: data.link_status,
    link_confidence: data.link_confidence,
    linked_by: data.linked_by,
    linked_at: data.linked_at,
    link_reasoning: data.link_reasoning,
    link_candidates: data.link_candidates,
  };
}

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function optionLine(option) {
  if (option.title) return option.title;
  const route = [option.pickup, option.drop].filter(Boolean).join(' → ');
  return route || 'No route on record';
}

function partyLine(option) {
  return [option.customer_name, option.customer_phone].filter(Boolean).join(' · ');
}

export default function ManualCallLinkButton({ log, onUpdated, relink = false }) {
  const [open, setOpen] = useState(false);
  const [entityType, setEntityType] = useState('trip');
  const [entityId, setEntityId] = useState('');
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoadingOptions(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${config.opsApiBase}/call-logs/${log.id}/link-options`,
          {
            params: { entity_type: entityType },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!cancelled) setOptions(response.data?.options || []);
      } catch (error) {
        if (!cancelled) {
          setOptions([]);
          toast.error(error.response?.data?.detail || 'Could not load records');
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, entityType, log.id]);

  if (relink ? !callCanRelink(log) : !callNeedsManualLink(log)) return null;

  const post = async (body, successText) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${config.opsApiBase}/call-logs/${log.id}/link`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdated(applyLinkUpdate(log, response.data || {}));
      setOpen(false);
      setEntityId('');
      toast.success(successText);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setSaving(false);
    }
  };

  const saveLink = () => {
    const id = parseInt(String(entityId).trim(), 10);
    if (!id) {
      toast.error('Select a record or enter its id');
      return;
    }
    const label = LINK_TYPES.find((item) => item.value === entityType)?.label || entityType;
    post(
      { action: 'link', entity_type: entityType, entity_id: id },
      `Linked to ${label} #${id}`
    );
  };

  const groups = options.reduce((acc, option) => {
    const name = option.group || 'Records';
    if (!acc[name]) acc[name] = [];
    acc[name].push(option);
    return acc;
  }, {});

  const modal = open ? createPortal(
    <div
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 80,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(560px, 100%)', maxHeight: '80vh', overflow: 'auto',
          background: '#fff', borderRadius: 14, padding: 20,
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{relink ? 'Re-link this call' : 'Link this call'}</div>
          <button type="button" onClick={() => setOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>
        <select
          value={entityType}
          onChange={(event) => { setEntityType(event.target.value); setEntityId(''); }}
          style={{
            width: '100%', marginBottom: 6, padding: '8px 10px',
            border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#f8fafc',
          }}
        >
          {LINK_TYPES.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>{hintFor(log, entityType)}</div>

        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, maxHeight: 320, overflow: 'auto', marginBottom: 12 }}>
          {loadingOptions && <div style={{ padding: 16, fontSize: 13, color: '#64748b' }}>Loading records…</div>}
          {!loadingOptions && options.length === 0 && (
            <div style={{ padding: 16, fontSize: 13, color: '#64748b' }}>
              Nothing matched this caller. Type the id below.
            </div>
          )}
          {!loadingOptions && Object.entries(groups).map(([group, rows]) => (
            <div key={group}>
              <div style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#64748b', background: '#f8fafc', textTransform: 'uppercase' }}>
                {group}
              </div>
              {rows.map((option) => {
                const selected = String(entityId) === String(option.id);
                return (
                  <button
                    key={`${group}-${option.id}`}
                    type="button"
                    onClick={() => setEntityId(String(option.id))}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '10px 12px', border: 'none', borderTop: '1px solid #f1f5f9',
                      background: selected ? '#eff6ff' : '#fff', cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 650, color: '#0f172a' }}>
                      #{option.id} · {optionLine(option)}
                    </div>
                    {partyLine(option) && (
                      <div style={{ fontSize: 12, color: '#0f172a', marginTop: 2 }}>
                        {partyLine(option)}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {[option.status, formatWhen(option.when)].filter(Boolean).join(' · ')}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
          Or type an id
        </label>
        <input
          type="number"
          min="1"
          placeholder="Record id"
          value={entityId}
          onChange={(event) => setEntityId(event.target.value)}
          style={{
            width: '100%', marginBottom: 14, padding: '8px 10px', boxSizing: 'border-box',
            border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14,
          }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            disabled={saving}
            onClick={saveLink}
            style={{
              flex: 1, padding: '10px 12px', border: 'none', borderRadius: 8,
              background: '#1d4ed8', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Save link
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => post({ action: 'done' }, 'Marked done')}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 8,
              border: '1px solid #e5e7eb', background: '#fff',
              color: '#334155', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Mark done
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div style={{ position: 'relative', flexShrink: 0 }} onClick={(event) => event.stopPropagation()}>
      <style>{`
        @keyframes callLinkHeartbeat {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.55); }
          45% { transform: scale(1.07); box-shadow: 0 0 0 7px rgba(220, 38, 38, 0); }
        }
      `}</style>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={relink ? {
          padding: '3px 8px',
          borderRadius: 5,
          border: '1px solid #cbd5e1',
          background: '#fff',
          color: '#1e293b',
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
        } : {
          padding: '3px 8px',
          borderRadius: 5,
          border: 'none',
          background: '#dc2626',
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
          animation: 'callLinkHeartbeat 1.15s ease-in-out infinite',
        }}
      >
        {relink ? 'Re-link' : 'Link'}
      </button>
      {modal}
    </div>
  );
}
